import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsMobileMenuOpen(false), [location]);

  const navLinks = [
    { href: '/#process', label: t.nav.process },
    { href: '/#faq', label: t.nav.faq },
    { href: '/rendez-vous', label: t.nav.contact },
  ];

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = href;
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-neutral-100'
            : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" data-testid="header-logo" className="flex-shrink-0">
              <img
                src={isScrolled
                  ? "https://customer-assets.emergentagent.com/job_4c369902-603a-4e2e-b32f-237481e35eb7/artifacts/6snptel5_Consultauto_Logo_Noir.png"
                  : "https://customer-assets.emergentagent.com/job_4c369902-603a-4e2e-b32f-237481e35eb7/artifacts/wjvsipix_Consultauto_Logo_Blanc.png"
                }
                alt="Consultauto"
                className="w-[210px] md:w-[240px] h-auto transition-all duration-500"
                width={240}
                height={40}
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`px-4 py-2 text-sm font-sans transition-colors duration-500 ${
                    isScrolled
                      ? 'text-neutral-400 hover:text-black'
                      : 'text-white/75 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-5">
              <a
                href="https://www.facebook.com/consultauto.ca"
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 transition-colors duration-300 ${
                  isScrolled ? 'text-neutral-400 hover:text-black' : 'text-white/70 hover:text-white'
                }`}
                aria-label="Facebook"
                data-testid="header-facebook"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <button
                onClick={toggleLanguage}
                data-testid="language-switcher"
                className={`px-2 py-1 text-xs font-sans font-medium uppercase tracking-wider transition-colors duration-500 ${
                  isScrolled ? 'text-neutral-400 hover:text-black' : 'text-white/70 hover:text-white'
                }`}
              >
                {language === 'fr' ? 'EN' : 'FR'}
              </button>

              <Link
                to="/rendez-vous"
                data-testid="header-book-cta"
                className={`rounded-full px-7 py-2.5 text-sm font-sans font-medium border transition-all duration-300 ${
                  isScrolled
                    ? 'bg-black text-white border-transparent hover:bg-white hover:text-black hover:border-black'
                    : 'bg-white text-black border-transparent hover:bg-transparent hover:text-white hover:border-white'
                }`}
              >
                {t.nav.bookCall}
              </Link>
            </div>

            <div className="flex lg:hidden items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`px-2 py-1 text-xs font-sans font-medium uppercase ${
                  isScrolled ? 'text-neutral-500' : 'text-white/80'
                }`}
              >
                {language === 'fr' ? 'EN' : 'FR'}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className={`w-5 h-5 ${isScrolled ? 'text-black' : 'text-white'}`} />
                ) : (
                  <Menu className={`w-5 h-5 ${isScrolled ? 'text-black' : 'text-white'}`} />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu — CSS transition, no framer-motion */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-menu"
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="absolute inset-0 bg-white pt-20">
          <nav className="container-custom py-8">
            <div className="flex flex-col">
              {navLinks.map((link, index) => (
                <div
                  key={link.href}
                  className="transition-all duration-300"
                  style={{
                    opacity: isMobileMenuOpen ? 1 : 0,
                    transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-15px)',
                    transitionDelay: isMobileMenuOpen ? `${index * 50}ms` : '0ms',
                  }}
                >
                  <Link
                    to={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="block py-5 text-lg font-sans font-medium border-b border-neutral-100 text-black"
                  >
                    {link.label}
                  </Link>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to="/rendez-vous"
                className="block w-full bg-black text-white text-center rounded-full py-4 text-base font-sans font-medium border border-transparent hover:bg-white hover:text-black hover:border-black transition-all duration-300"
                data-testid="mobile-book-cta"
              >
                {t.nav.bookCall}
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
